import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import type { ErrorRecord, ErrorCategory, ErrorRecordStatus, ErrorPattern } from '../../../shared/types-error-recovery';

const ACCENT = '#f97316';

const CATEGORY_COLORS: Record<ErrorCategory, string> = {
  validation: '#ef4444',
  connection: '#f59e0b',
  timeout: '#8b5cf6',
  conflict: '#ec4899',
  permission: '#dc2626',
  transform: '#3b82f6',
  unknown: '#6b7280',
};

const STATUS_COLORS: Record<ErrorRecordStatus, string> = {
  pending: '#f59e0b',
  retrying: '#3b82f6',
  resolved: '#10b981',
  skipped: '#6b7280',
  manually_fixed: '#8b5cf6',
};

interface ErrorStats {
  by_category: { category: string; count: number }[];
  by_source: { source: string; count: number }[];
  by_status: { status: string; count: number }[];
  errors_today: number;
  trend: { date: string; count: number }[];
  resolution_rate: number;
  most_common_category: string;
  total: number;
}

interface ErrorListResponse {
  data: ErrorRecord[];
  total: number;
  offset: number;
  limit: number;
}

interface PatternsResponse {
  patterns: ErrorPattern[];
}

function Badge({ label, color }: { label: string; color: string }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 12,
      fontSize: '0.75rem',
      fontWeight: 600,
      backgroundColor: `${color}18`,
      color,
      textTransform: 'capitalize',
    }}>
      {label}
    </span>
  );
}

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div style={{
      backgroundColor: '#fff',
      borderRadius: 8,
      padding: '1rem 1.25rem',
      border: '1px solid #e5e7eb',
      minWidth: 140,
    }}>
      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>{value}</div>
      {sub && <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function ErrorRecoveryPage() {
  const [errors, setErrors] = useState<ErrorRecord[]>([]);
  const [stats, setStats] = useState<ErrorStats | null>(null);
  const [patterns, setPatterns] = useState<ErrorPattern[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showPatterns, setShowPatterns] = useState(false);
  const [fixModal, setFixModal] = useState<ErrorRecord | null>(null);
  const [fixJson, setFixJson] = useState('');
  const [fixNote, setFixNote] = useState('');
  const [fixError, setFixError] = useState('');

  // Filters
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [search, setSearch] = useState('');

  const LIMIT = 50;

  const buildQuery = useCallback(() => {
    const parts: string[] = [];
    if (category) parts.push(`category=${category}`);
    if (status) parts.push(`status=${status}`);
    if (source) parts.push(`source=${source}`);
    if (search) parts.push(`search=${encodeURIComponent(search)}`);
    parts.push(`offset=${offset}`);
    parts.push(`limit=${LIMIT}`);
    return parts.join('&');
  }, [category, status, source, search, offset]);

  const fetchErrors = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<ErrorListResponse>(`/errors?${buildQuery()}`);
      setErrors(res.data);
      setTotal(res.total);
    } catch { /* ignore */ }
    setLoading(false);
  }, [buildQuery]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get<ErrorStats>('/errors/stats');
      setStats(res);
    } catch { /* ignore */ }
  }, []);

  const fetchPatterns = useCallback(async () => {
    try {
      const res = await api.get<PatternsResponse>('/errors/patterns');
      setPatterns(res.patterns);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchErrors(); }, [fetchErrors]);
  useEffect(() => { fetchStats(); fetchPatterns(); }, [fetchStats, fetchPatterns]);

  const handleRetry = async (id: string) => {
    setActionLoading(id);
    try {
      await api.post(`/errors/${id}/retry`);
      await fetchErrors();
      await fetchStats();
    } catch { /* ignore */ }
    setActionLoading(null);
  };

  const handleSkip = async (id: string) => {
    setActionLoading(id);
    try {
      await api.post(`/errors/${id}/skip`);
      await fetchErrors();
      await fetchStats();
    } catch { /* ignore */ }
    setActionLoading(null);
  };

  const handleBulkRetry = async () => {
    setActionLoading('bulk-retry');
    try {
      await api.post('/errors/bulk-retry', { category: category || undefined, source: source || undefined });
      await fetchErrors();
      await fetchStats();
    } catch { /* ignore */ }
    setActionLoading(null);
  };

  const handleBulkSkip = async () => {
    setActionLoading('bulk-skip');
    try {
      await api.post('/errors/bulk-skip', { category: category || undefined, source: source || undefined });
      await fetchErrors();
      await fetchStats();
    } catch { /* ignore */ }
    setActionLoading(null);
  };

  const openFixModal = (record: ErrorRecord) => {
    setFixModal(record);
    setFixJson(JSON.stringify(record.record_data, null, 2));
    setFixNote('');
    setFixError('');
  };

  const handleFix = async () => {
    if (!fixModal) return;
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(fixJson);
    } catch {
      setFixError('Invalid JSON');
      return;
    }
    setActionLoading('fix');
    try {
      await api.post(`/errors/${fixModal.id}/fix`, { record_data: parsed, resolution_note: fixNote || undefined });
      setFixModal(null);
      await fetchErrors();
      await fetchStats();
    } catch (e: any) {
      setFixError(e.message || 'Fix failed');
    }
    setActionLoading(null);
  };

  const pendingCount = stats?.by_status?.find(s => s.status === 'pending')?.count ?? 0;
  const resolvedCount = (stats?.by_status?.find(s => s.status === 'resolved')?.count ?? 0) +
    (stats?.by_status?.find(s => s.status === 'manually_fixed')?.count ?? 0);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0, color: '#111827' }}>Error Recovery</h1>
          <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.875rem' }}>
            Monitor, retry, and resolve failed operations
          </p>
        </div>
      </div>

      {/* Stats cards */}
      {stats && (
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          <StatCard label="Total Errors" value={stats.total} />
          <StatCard label="Errors Today" value={stats.errors_today} />
          <StatCard label="Pending" value={pendingCount} />
          <StatCard label="Resolved" value={resolvedCount} />
          <StatCard label="Resolution Rate" value={`${stats.resolution_rate}%`} />
          <StatCard label="Most Common" value={stats.most_common_category} />
        </div>
      )}

      {/* Category summary bar */}
      {stats && stats.by_category.length > 0 && (
        <div style={{
          display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap',
        }}>
          {stats.by_category.map(c => (
            <Badge
              key={c.category}
              label={`${c.category}: ${c.count}`}
              color={CATEGORY_COLORS[c.category as ErrorCategory] || '#6b7280'}
            />
          ))}
        </div>
      )}

      {/* Filter row */}
      <div style={{
        display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center',
      }}>
        <select
          value={category}
          onChange={e => { setCategory(e.target.value); setOffset(0); }}
          style={selectStyle}
        >
          <option value="">All Categories</option>
          {(['validation', 'connection', 'timeout', 'conflict', 'permission', 'transform', 'unknown'] as ErrorCategory[]).map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        <select
          value={status}
          onChange={e => { setStatus(e.target.value); setOffset(0); }}
          style={selectStyle}
        >
          <option value="">All Statuses</option>
          {(['pending', 'retrying', 'resolved', 'skipped', 'manually_fixed'] as ErrorRecordStatus[]).map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        <select
          value={source}
          onChange={e => { setSource(e.target.value); setOffset(0); }}
          style={selectStyle}
        >
          <option value="">All Sources</option>
          <option value="import">Import</option>
          <option value="sync">Sync</option>
          <option value="flow">Flow</option>
          <option value="webhook">Webhook</option>
        </select>

        <input
          type="text"
          placeholder="Search error messages..."
          value={search}
          onChange={e => { setSearch(e.target.value); setOffset(0); }}
          style={{
            padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db',
            fontSize: '0.875rem', flex: 1, minWidth: 180,
          }}
        />
      </div>

      {/* Bulk actions toolbar */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <button
          onClick={handleBulkRetry}
          disabled={actionLoading === 'bulk-retry'}
          style={{ ...btnStyle, backgroundColor: ACCENT, color: '#fff' }}
        >
          {actionLoading === 'bulk-retry' ? 'Retrying...' : `Retry All Pending (${pendingCount})`}
        </button>
        <button
          onClick={handleBulkSkip}
          disabled={actionLoading === 'bulk-skip'}
          style={{ ...btnStyle, backgroundColor: '#6b7280', color: '#fff' }}
        >
          {actionLoading === 'bulk-skip' ? 'Skipping...' : `Skip All (${pendingCount})`}
        </button>
      </div>

      {/* Error table */}
      <div style={{ backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'auto', marginBottom: '1.5rem' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <th style={thStyle}>Entity</th>
              <th style={thStyle}>Source</th>
              <th style={thStyle}>Error</th>
              <th style={thStyle}>Category</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Retries</th>
              <th style={thStyle}>Created</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Loading...</td></tr>
            ) : errors.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>No errors found</td></tr>
            ) : errors.map(err => (
              <tr key={err.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={tdStyle}>{err.entity_type}</td>
                <td style={tdStyle}>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{err.source}</span>
                  <br />
                  <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{err.source_name}</span>
                </td>
                <td style={{ ...tdStyle, maxWidth: 300 }}>
                  <span title={err.error_message} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: 300 }}>
                    {err.error_message}
                  </span>
                </td>
                <td style={tdStyle}>
                  <Badge label={err.category} color={CATEGORY_COLORS[err.category] || '#6b7280'} />
                </td>
                <td style={tdStyle}>
                  <Badge label={err.status} color={STATUS_COLORS[err.status] || '#6b7280'} />
                </td>
                <td style={tdStyle}>{err.retry_count}/{err.max_retries}</td>
                <td style={tdStyle}>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    {new Date(err.created_at).toLocaleDateString()}
                  </span>
                </td>
                <td style={tdStyle}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {err.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleRetry(err.id)}
                          disabled={actionLoading === err.id}
                          style={{ ...smallBtnStyle, color: ACCENT, borderColor: ACCENT }}
                        >
                          Retry
                        </button>
                        <button
                          onClick={() => handleSkip(err.id)}
                          disabled={actionLoading === err.id}
                          style={{ ...smallBtnStyle, color: '#6b7280', borderColor: '#d1d5db' }}
                        >
                          Skip
                        </button>
                      </>
                    )}
                    {(err.status === 'pending' || err.status === 'retrying') && (
                      <button
                        onClick={() => openFixModal(err)}
                        style={{ ...smallBtnStyle, color: '#8b5cf6', borderColor: '#8b5cf6' }}
                      >
                        Fix
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > LIMIT && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
          <button
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
            disabled={offset === 0}
            style={btnStyle}
          >
            Previous
          </button>
          <span style={{ alignSelf: 'center', fontSize: '0.875rem', color: '#6b7280' }}>
            {offset + 1}-{Math.min(offset + LIMIT, total)} of {total}
          </span>
          <button
            onClick={() => setOffset(offset + LIMIT)}
            disabled={offset + LIMIT >= total}
            style={btnStyle}
          >
            Next
          </button>
        </div>
      )}

      {/* Error patterns panel */}
      <div style={{
        backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', marginBottom: '1.5rem',
      }}>
        <button
          onClick={() => setShowPatterns(!showPatterns)}
          style={{
            width: '100%', padding: '0.75rem 1rem', background: 'none', border: 'none',
            cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            fontSize: '0.875rem', fontWeight: 600, color: '#111827',
          }}
        >
          <span>Error Patterns ({patterns.length})</span>
          <span>{showPatterns ? '\u25B2' : '\u25BC'}</span>
        </button>
        {showPatterns && (
          <div style={{ padding: '0 1rem 1rem' }}>
            {patterns.length === 0 ? (
              <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>No patterns detected yet.</p>
            ) : patterns.map(p => (
              <div key={p.id} style={{
                padding: '0.75rem', borderRadius: 6, border: '1px solid #f3f4f6',
                marginBottom: '0.5rem', backgroundColor: '#fafafa',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{p.pattern}</span>
                  <Badge label={p.category} color={CATEGORY_COLORS[p.category] || '#6b7280'} />
                </div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                  {p.occurrence_count} occurrences | Last seen: {new Date(p.last_seen).toLocaleDateString()}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#059669', marginTop: 4 }}>
                  Suggested fix: {p.suggested_fix}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Fix modal */}
      {fixModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: 12, padding: '1.5rem',
            width: '90%', maxWidth: 600, maxHeight: '80vh', overflow: 'auto',
          }}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 700, margin: '0 0 0.5rem' }}>
              Fix Error: {fixModal.entity_type}
            </h2>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
              {fixModal.error_message}
            </p>

            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: 4, color: '#374151' }}>
              Record Data (JSON)
            </label>
            <textarea
              value={fixJson}
              onChange={e => { setFixJson(e.target.value); setFixError(''); }}
              style={{
                width: '100%', minHeight: 200, fontFamily: 'monospace', fontSize: '0.8rem',
                padding: '0.75rem', borderRadius: 6, border: '1px solid #d1d5db', resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />

            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: 4, marginTop: '0.75rem', color: '#374151' }}>
              Resolution Note (optional)
            </label>
            <input
              type="text"
              value={fixNote}
              onChange={e => setFixNote(e.target.value)}
              placeholder="Describe what was fixed..."
              style={{
                width: '100%', padding: '6px 12px', borderRadius: 6,
                border: '1px solid #d1d5db', fontSize: '0.875rem', boxSizing: 'border-box',
              }}
            />

            {fixError && (
              <p style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.5rem' }}>{fixError}</p>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <button onClick={() => setFixModal(null)} style={btnStyle}>Cancel</button>
              <button
                onClick={handleFix}
                disabled={actionLoading === 'fix'}
                style={{ ...btnStyle, backgroundColor: '#8b5cf6', color: '#fff', borderColor: '#8b5cf6' }}
              >
                {actionLoading === 'fix' ? 'Saving...' : 'Save & Fix'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const selectStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid #d1d5db',
  fontSize: '0.875rem',
  backgroundColor: '#fff',
};

const thStyle: React.CSSProperties = {
  padding: '0.625rem 0.75rem',
  textAlign: 'left',
  fontWeight: 600,
  color: '#374151',
  fontSize: '0.8rem',
};

const tdStyle: React.CSSProperties = {
  padding: '0.625rem 0.75rem',
  color: '#111827',
};

const btnStyle: React.CSSProperties = {
  padding: '6px 14px',
  borderRadius: 6,
  border: '1px solid #d1d5db',
  backgroundColor: '#fff',
  fontSize: '0.875rem',
  cursor: 'pointer',
  fontWeight: 500,
};

const smallBtnStyle: React.CSSProperties = {
  padding: '3px 8px',
  borderRadius: 4,
  border: '1px solid',
  backgroundColor: 'transparent',
  fontSize: '0.75rem',
  cursor: 'pointer',
  fontWeight: 500,
};
